<template>
  <ModalCard :isOpen="isAlwaysOpen ? true : isOpenModal" :modalCardClass="modalMessageClasses" :canCancel="false">
    <template #modal-card-content>
      <div class="pt-[40px] px-[48px] pb-[48px]">
        <header class="modal-message__header align-center">
          <BaseIconSVG
            v-if="hasIcon && iconMessage"
            :class="`mr-2 fill-${iconMessage.color}`"
            :name="iconMessage.name"
            :width="32"
            :height="32"
          >
          </BaseIconSVG>
          <!-- <div v-else style="min-width: 32px"></div> -->
          <div class="has-font-size-24 has-font-weight-600 leading-[30px] text-gray-9">{{ translate(title) }}</div>
        </header>
        <div class="modal-message__content my-4" :class="hasIcon && iconMessage ? 'has-icon' : ''">
          <p
            class="modal-message__content-text has-font-size-16 leading-[20px] font-400 text-gray-9 whitespace-pre-line"
            v-html="message"
          />
        </div>
        <footer
          v-if="showFooter || hasCancel"
          :class="['flex items-center justify-end mt-[40px] gap-16px', footerClasses]"
        >
          <BaseButton v-if="hasCancel" variant="outlined" :size="40" @click="handleCancel">
            {{ translate(cancelText) }}
          </BaseButton>
          <div class="flex gap-16px">
            <BaseButton v-if="extraButtonText" variant="outlined" :size="40" @click="handleExtraButton">
              {{ translate(extraButtonText) }}
            </BaseButton>
            <BaseButton
              buttonClass="Components__ModalMessage__ConfirmButton"
              variant="filled"
              :size="40"
              @click="handleConfirmModal"
            >
              {{ translate(confirmText) }}
            </BaseButton>
          </div>
        </footer>
      </div>
    </template>
  </ModalCard>
</template>

<script>
import cn from 'classnames'
import ModalCard from './ModalCard'
import BaseIconSVG from '~/components/v2/DataDisplay/BaseIconSVG'
import BaseButton from '~/components/v2/Inputs/BaseButton.vue'

export default {
  name: 'AcademyModalMessage',
  components: { BaseButton, BaseIconSVG, ModalCard },
  props: {
    modalClasses: {
      type: String,
      default() {
        return ''
      },
    },
    footerClasses: {
      type: String,
      default: '',
    },
    isAlwaysOpen: {
      type: Boolean,
      default() {
        return false
      },
    },
    title: {
      type: String,
      default() {
        return 'General'
      },
    },
    message: {
      type: String,
      default() {
        return ''
      },
    },
    closeable: {
      type: Boolean,
      default() {
        return true
      },
    },
    showFooter: {
      type: Boolean,
      default() {
        return false
      },
    },
    hasIcon: {
      type: Boolean,
      default() {
        return false
      },
    },
    hasCancel: {
      type: Boolean,
      default() {
        return true
      },
    },
    icon: {
      type: String,
      default() {
        return ''
      },
    },
    type: {
      type: String,
      default() {
        // types: is-general, is-info, is-error, is-success, is-warning
        return 'is-general'
      },
    },
    confirmText: {
      type: String,
      default() {
        return 'Confirm'
      },
    },
    cancelText: {
      type: String,
      default() {
        return 'Cancel'
      },
    },
    extraButtonText: {
      type: String,
      default() {
        return ''
      },
    },
    confirmAsync: {
      type: Boolean,
      default() {
        return false
      },
    },
    extraFunction: {
      type: Function,
      default() {
        return () => {}
      },
    },
    onConfirm: {
      type: Function,
      default() {
        return () => {}
      },
    },
    onCancel: {
      type: Function,
      default() {
        return () => {}
      },
    },
    onClose: {
      type: Function,
      default() {
        return () => {}
      },
    },
  },
  data() {
    return {
      isOpenModal: false,
    }
  },
  computed: {
    iconMessage() {
      switch (this.type) {
        case 'is-info':
          return { name: 'info', color: '#ccc' }
        case 'is-success':
          return { name: 'checked', color: '#a5cd39' }
        case 'is-error':
          return { name: 'error', color: 'strawberry-10' }
        default:
          if (this.icon) {
            return { name: this.icon, color: '#ccc' }
          }
          return null
      }
    },
    modalMessageClasses() {
      const modalTypeClass = this.type || ''
      return cn('academy-modal-message', modalTypeClass, this.modalClasses)
    },
  },
  methods: {
    setOpenModal(isOpenModal) {
      if (!isOpenModal) {
        this.handleClose()
      } else {
        this.isOpenModal = isOpenModal
      }
    },
    handleExtraButton() {
      if (this.confirmAsync) {
        this.extraFunction(this.setOpenModal)
      } else {
        this.extraFunction()
        this.handleClose()
      }
    },
    handleConfirmModal() {
      if (this.confirmAsync) {
        this.onConfirm(this.setOpenModal)
      } else {
        this.onConfirm()
        this.handleClose()
      }
    },
    handleCancel() {
      this.onCancel()
      this.handleClose()
    },
    handleClose() {
      this.isOpenModal = false
      setTimeout(() => {
        this.$destroy()
        if (typeof this.$el.remove !== 'undefined') {
          this.$el.remove()
        } else if (typeof this.$el.parentNode !== 'undefined') {
          this.$el.parentNode.removeChild(this.$el)
        }
      }, 150)
    },
    translate(text) {
      try {
        if (!text) return ''
        if (!this.$t) return text
        return this.$t(text)
      } catch (error) {
        return text
      }
    },
  },
  beforeMount() {
    if (typeof window !== 'undefined') {
      this.$nextTick(() => {
        document.body.appendChild(this.$el)
      })
    }
  },
  mounted() {
    this.isOpenModal = true
  },
}
</script>

<style lang="scss">
.academy-modal-card.academy-modal-message {
  .content {
    margin-bottom: 0px;
  }

  .card-content {
    padding: unset;
  }

  .modal-message__close-btn {
    display: inline-flex;
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;
    color: $color-inactive;
  }

  .modal-message__header {
    display: flex;
    flex-direction: row;

    .academy-svg-icon {
      margin-right: 10px;
      position: relative;
      top: -5px;
    }
  }

  .modal-message__content {
    &.has-icon {
      margin-left: 42px;
    }
  }

  .modal-message__footer {
    justify-content: flex-end;
    display: flex;

    .academy-button:last-child {
      margin-left: 16px;
    }
  }

  .card.academy-custom-card {
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.4);
  }

  &.is-error {
    .card.academy-custom-card {
      background-color: #f8f8f8;
    }
  }

  &.is-success {
    .card.academy-custom-card {
      background-color: #f8f8f8;
    }
  }

  &.request-update-trainer-modal {
    .card-content {
      max-width: 640px;
    }
  }
}
</style>
